import React from "react";
import { Star, Building, Clock, TrendingUp } from "lucide-react";
import Image from "next/image";
import { z } from "zod";

const TestimonialSchema = z.object({
	name: z.string(),
	role: z.string(),
	company: z.string(),
	content: z.string(),
	rating: z.number(),
	image: z.string(),
});

const StatSchema = z.object({
	icon: z.any(),
	value: z.string(),
	label: z.string(),
});

const SocialProofSchema = z.object({
	header: z.string().default("Trusted by Architects Worldwide"),
	description: z
		.string()
		.default(
			"Join thousands of professionals who've transformed their BIM workflows with PropTechBuzz."
		),
	testimonials: z.array(TestimonialSchema).default([
		{
			name: "Sarah Al-Rashid",
			role: "Senior Architect, Dubai",
			company: "Emirates Architecture",
			content:
				"PropTechBuzz connected us with the perfect BIM specialist for our mixed-use development. The quality of work exceeded our expectations.",
			rating: 5,
			image:
				"https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
		},
		{
			name: "Marcus Weber",
			role: "Project Manager, Berlin",
			company: "Deutsche Bau Solutions",
			content:
				"The platform's vendor matching algorithm is impressive. We found specialized Revit experts who delivered our hospital project on time.",
			rating: 5,
			image:
				"https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
		},
		{
			name: "Ahmed Hassan",
			role: "BIM Coordinator, Riyadh",
			company: "Saudi Vision Construction",
			content:
				"The community aspect is fantastic. I've learned so much from other architects and improved our BIM workflows significantly.",
			rating: 5,
			image:
				"https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
		},
	]),
	stats: z.array(StatSchema).default([
		{
			icon: TrendingUp,
			value: "$45M+",
			label: "Project Value Facilitated",
		},
		{
			icon: Star,
			value: "8.9/10",
			label: "Average Vendor Rating",
		},
		{
			icon: Clock,
			value: "98%",
			label: "On-Time Delivery",
		},
		{
			icon: Building,
			value: "1,200+",
			label: "Projects Completed",
		},
	]),
});

type SocialProofProps = z.input<typeof SocialProofSchema>;

const SocialProof: React.FC<SocialProofProps> = (props = {}) => {
	const data = SocialProofSchema.parse(props);

	return (
		<section className="bg-gray-50 py-20">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* Stats Section */}
				<div className="mb-16 grid gap-8 md:grid-cols-4">
					{data.stats.map((stat, index) => (
						<div key={index} className="text-center">
							<div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-primary-500">
								<stat.icon className="h-8 w-8 text-white" />
							</div>
							<div className="mb-2 text-3xl font-bold text-gray-900">{stat.value}</div>
							<div className="text-gray-600">{stat.label}</div>
						</div>
					))}
				</div>

				{/* Testimonials Section */}
				<div className="mb-12 text-center">
					<h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">{data.header}</h2>
					<p className="mx-auto max-w-3xl text-xl text-gray-600">{data.description}</p>
				</div>

				<div className="grid gap-8 md:grid-cols-3">
					{data.testimonials.map((testimonial, index) => (
						<div
							key={index}
							className="rounded-xl bg-white p-6 shadow-lg transition-shadow hover:shadow-xl"
						>
							<div className="mb-4 flex items-center">
								<Image
									src={testimonial.image}
									alt={testimonial.name}
									className="mr-4 h-12 w-12 rounded-full object-cover"
									width={48}
									height={48}
								/>
								<div>
									<h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
									<p className="text-sm text-gray-600">{testimonial.role}</p>
									<p className="text-sm text-primary-500">{testimonial.company}</p>
								</div>
							</div>

							<div className="mb-4 flex">
								{[...Array(testimonial.rating)].map((_, i) => (
									<Star key={i} className="h-5 w-5 fill-current text-yellow-400" />
								))}
							</div>

							<blockquote className="italic leading-relaxed text-gray-700">
								&quot;{testimonial.content}&quot;
							</blockquote>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default SocialProof;
